import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { staffService } from '../../services';
import {
  Modal, Button, Select, Loader, WeekPatternGrid,
  defaultWeekPattern, weeklyPaidHours,
} from '../../components';

/**
 * Manage roster — one staff member's working week.
 *
 * Opens on WHAT THEY HAVE, not on a blank week. The old dialog here asked for a
 * group, a set of day names and one start/end time for all of them, which threw
 * away the per-day pattern every other screen in the app now keeps: a manager
 * who opened it to change Tuesday flattened the rest of the week to a single
 * shift without being told.
 *
 * It uses WeekPatternGrid and the same payload as Add Staff and the Recruitment
 * transfer, so a roster set here and one set there are the same thing. The
 * legacy working_days / shift_start / shift_end columns are still written — the
 * API derives them from the pattern on every save (see utils/weekPattern.js),
 * so the ten readers that only know about those keep working.
 *
 * @param {object}   staff        the row this was opened from ({ id, name })
 * @param {Array}    groups       teams to choose from
 * @param {boolean}  domiciliary  availability rather than shifts
 * @param {Function} onSaved      called after a successful save
 */
const inputCls = 'w-full px-4 py-2.5 bg-white dark:bg-[#31373f] dark:text-gray-200 border border-[#CBD5E1] dark:border-gray-700 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]';
const labelCls = 'block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5';

const ManageRosterModal = ({
  isOpen, onClose, staff, groups = [], domiciliary = false, onSaved, onGroupCreated,
}) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  // Load their actual roster every time it opens. Not cached: a roster changed
  // from Add & Edit Staff in another tab would otherwise be saved back over
  // from a stale copy held here.
  useEffect(() => {
    if (!isOpen || !staff?.id) return;
    let cancelled = false;

    setForm(null);
    setLoading(true);
    setCreatingGroup(false);
    setNewGroupName('');

    staffService.getStaffRoster(staff.id)
      .then((response) => {
        if (cancelled) return;
        // No roster row at all — a new starter who was never given one. The
        // usual Monday-to-Friday shape is a starting point, not a claim about
        // what they work.
        const roster = response?.data || {};
        setForm({
          group_id: roster.group_id ? String(roster.group_id) : '',
          contracted_hours: roster.contracted_hours ?? '',
          shift_name: roster.shift_name || 'Day',
          week_pattern: Array.isArray(roster.week_pattern) && roster.week_pattern.length
            ? roster.week_pattern
            : defaultWeekPattern(),
        });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Could not load their current roster');
        setForm({
          group_id: '', contracted_hours: '', shift_name: 'Day', week_pattern: defaultWeekPattern(),
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, staff?.id]);

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const response = await staffService.createStaffGroup({ name });
      if (response?.success) {
        toast.success('Team created');
        patch({ group_id: String(response.data.id) });
        setCreatingGroup(false);
        setNewGroupName('');
        onGroupCreated?.();
      }
    } catch (error) {
      toast.error(error?.message || 'Could not create the team');
    }
  };

  const submit = async () => {
    const working = (form.week_pattern || []).filter((day) => day.on);
    if (!working.length) {
      toast.error(domiciliary ? 'Mark at least one day they are available' : 'Select at least one working day');
      return;
    }
    try {
      setSaving(true);
      await staffService.upsertStaffRoster(staff.id, {
        week_pattern: form.week_pattern,
        pattern_mode: domiciliary ? 'DOMICILIARY' : 'CARE_HOME',
        contracted_hours: form.contracted_hours,
        group_id: form.group_id || null,
        shift_name: form.shift_name,
      });
      toast.success(`Roster updated for ${staff.name}`);
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Could not save the roster');
    } finally {
      setSaving(false);
    }
  };

  const pattern = form?.week_pattern || [];
  const workingDays = pattern.filter((day) => day.on).length;
  const paidHours = weeklyPaidHours(pattern);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${domiciliary ? 'Availability' : 'Roster'}: ${staff?.name || ''}`}
      maxWidth="max-w-4xl"
      footer={(
        <div className="flex justify-end gap-2.5">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!form} onClick={submit}>
            Save roster
          </Button>
        </div>
      )}
    >
      {loading || !form ? (
        <div className="py-12"><Loader /></div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[13px] text-[#64748B] dark:text-gray-400">
              {domiciliary
                ? 'The days and hours they are available. Visits are assigned later in Rota.'
                : 'The days and times they actually work. Each day can differ.'}
            </p>
            <span className="text-[12.5px] text-[#64748B] dark:text-gray-400 tabular-nums">
              {workingDays} {domiciliary ? 'available' : 'working'} day{workingDays === 1 ? '' : 's'}
              {!domiciliary && ` · ${paidHours.toFixed(1)} h/week`}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400">Team</label>
                <button
                  type="button"
                  onClick={() => setCreatingGroup(!creatingGroup)}
                  className="text-[12px] font-bold text-[#2563EB] hover:underline"
                >
                  {creatingGroup ? 'Cancel' : '+ New team'}
                </button>
              </div>
              {creatingGroup ? (
                <div className="flex gap-2">
                  <input
                    type="text" className={inputCls} placeholder="Team name..."
                    value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                  />
                  <Button type="button" variant="primary" onClick={createGroup}>Add</Button>
                </div>
              ) : (
                <Select
                  className="w-full"
                  value={form.group_id}
                  onChange={(value) => patch({ group_id: value })}
                  placeholder="Choose a team"
                  options={groups.map((group) => ({ value: String(group.id), label: group.name }))}
                />
              )}
            </div>

            <div>
              <label className={labelCls} htmlFor="roster-hours">Contracted weekly hours</label>
              <input
                id="roster-hours" type="number" min="0" step="0.5" className={inputCls}
                placeholder="e.g. 37.5"
                value={form.contracted_hours}
                onChange={(e) => patch({ contracted_hours: e.target.value })}
              />
            </div>

            {/* A domiciliary service has no Day/Night shift to name — the
                availability grid is the whole answer. */}
            {!domiciliary && (
              <div>
                <label className={labelCls} htmlFor="roster-shift">Shift name</label>
                <Select
                  className="w-full"
                  value={form.shift_name}
                  onChange={(value) => patch({ shift_name: value })}
                  options={[
                    { value: 'Day', label: 'Day' },
                    { value: 'Night', label: 'Night' },
                    { value: 'Evening', label: 'Evening' },
                  ]}
                />
              </div>
            )}
          </div>

          <WeekPatternGrid
            value={form.week_pattern}
            onChange={(week_pattern) => patch({ week_pattern })}
            domiciliary={domiciliary}
          />
        </div>
      )}
    </Modal>
  );
};

export default ManageRosterModal;
